import { Button, PageHeader, Table } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-date-picker';
import { toast } from 'react-toastify';
import Select from 'react-select';
import moment from 'moment';
import useStockingDashboardFilters, {
  STOP_TYPES,
} from '../hooks/useStockingDashboardFilters';
import useGetRetailers from '../hooks/useGetRetailers';
import 'moment-timezone';
import {
  GetLocationRoutes,
  FridgeHealthStockingDash,
  GetOpsLocationsStockingDash,
  PaginatedPackingSlip,
} from '../libs/GraphQL';
import { fetchDriverActionsForDate } from '../libs/stopStatus';
import LoaderButton from '../components/LoaderButton';
import yyyymmdd from '../libs/date_convert';
import './ViewStockingDashboard.css';
import TextLabel from '../components/atoms/TextLabel';
import RefreshIcon from '../assets/RefreshIcon';
import { formatAccountTier } from '../utils/accountTier';
import { prettyToastOpts } from '../utils/toast';

const toastDefaultOptions = prettyToastOpts({ autoClose: 7000 });

const STATUSES = {
  NOT_STARTED: {
    label: 'Not Started Yet',
    color: 'gray',
  },
  VAN_TEMPERATURE_SET: {
    label: 'Van Temperature Set',
    color: 'yellow',
    description:
      'Van temperature has been set but packing slip not yet scanned',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'yellow',
    description: 'Location has been scanned but not yet stocked',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'green',
    description: 'Location has been scanned and stocked successfully',
  },
  INCOMPLETE: {
    label: 'Incomplete',
    color: 'red',
    description:
      'Location has been scanned, not stocked and another location has been scanned after it',
  },
  UNREACHABLE_INCOMPLETE: {
    label: 'Unreachable Incomplete',
    color: 'red',
    description: 'Location is unreachable and has not been scanned',
  },
  UNREACHABLE_COMPLETE: {
    label: 'Unreachable Complete',
    color: 'green',
    description: 'Location is unreachable and has been scanned',
  },
  CLEAROUT_COMPLETE: {
    label: 'Removal Complete',
    color: 'green',
    description: 'Removal location has been completed',
  },
  CLEAROUT_DECOMMISSION_COMPLETE: {
    label: 'Decommision Complete',
    color: 'green',
    description: 'Clearout Decommission location has been completed',
  },
};

const VENDING_STATUSES = {
  STATUS_0_READY: {
    label: 'READY',
    color: 'lightGreen',
    description: 'STATUS_0_READY',
  },
  STATUS_FF_DOOR_CLOSE: {
    label: 'DOOR_CLOSE',
    color: 'lightBlue',
    description: 'STATUS_FF_DOOR_CLOSE',
  },
  STATUS_FF_DOOR_OPEN: {
    label: 'DOOR_OPEN',
    color: 'lightBlue',
    description: 'STATUS_FF_DOOR_OPEN',
  },
  STATUS_FF_TIMEOUT: {
    label: 'TIMEOUT',
    color: 'lightRed',
    description: 'STATUS_FF_TIMEOUT',
  },
  STATUS_B_PRODUCT_UNDER_ELEVATOR: {
    label: 'PRODUCT_UNDER_ELEVATOR',
    color: 'lightYellow',
    description: 'STATUS_B_PRODUCT_UNDER_ELEVATOR',
  },
  STATUS_9_PRODUCT_DETECT_FAULT: {
    label: 'PRODUCT_DETECT_FAULT',
    color: 'lightYellow',
    description: 'STATUS_9_PRODUCT_DETECT_FAULT',
  },
  STATUS_5_MOTOR_FAULT: {
    label: 'MOTOR_FAULT',
    color: 'lightRed',
    description: 'STATUS_5_MOTOR_FAULT',
  },
};

const regionsList = [
  { value: 'ALL', label: 'ALL' },
  { value: 'MW', label: 'MW' },
  { value: 'NE', label: 'NE' },
  { value: 'WC', label: 'WC' },
  { value: 'S', label: 'S' },
];

const ViewStockingDashboard = () => {
  const now = new Date();
  const { filters, setFilter, setFilters, resetFilters } =
    useStockingDashboardFilters(now);
  const { retailerList, loading: retailersLoading } = useGetRetailers();
  const handleRetailerFilterChange = (value) => {
    setFilter('retailerFilter', value);
  };
  const [routesList, setRoutesList] = useState([]);
  const [locationRoutes, setLocationRoutes] = useState([]);
  const [uniqueLocationRoutes, setUniqueLocationRoutes] = useState([]);
  const [searchedRoutes, setSearchedRoutes] = useState([]);
  const [filterSearched, setFilterSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [packingSlipsList, setPackingSlipsList] = useState([]);
  const [fulfillmentCentersList, setFulfillmentCentersList] = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [prefetchedLocationsData, setPrefetchedLocationsData] = useState(null);
  const [prefetchedDate, setPrefetchedDate] = useState(null);
  const [prefetchLoading, setPrefetchLoading] = useState(false);

  const intervalRef = useRef();

  const startInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(handleSearch, 120000);
  };

  const stopInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const loadLocationAndPackingSlips = async (date) => {
    setIsLoading(true);
    let uniqueRoutesList = [];

    try {
      await Promise.all([
        getLocations(date)
          .then((routes) => {
            uniqueRoutesList = routes;
          })
          .catch((error) => {
            console.error('Failed to get locations:', error);
          }),

        getPackingSlips(yyyymmdd(date, '-')).catch((error) => {
          console.error('Failed to get packing slips:', error);
        }),
      ]);
    } catch (error) {
      console.error('Error during loading locations and packing slips:', error);
    } finally {
      setIsLoading(false);
    }
    return uniqueRoutesList;
  };

  const prefetchDataForDate = async (date) => {
    setPrefetchedLocationsData(null);
    setPrefetchedDate(null);
    setPrefetchLoading(true);

    try {
      const queriedLocationsData = await fetchStockedAndQueriedLocations(date);

      setPrefetchedLocationsData(queriedLocationsData);
      setPrefetchedDate(date);
    } catch (err) {
      console.error('Failed to prefetch data for date:', err);
    } finally {
      setPrefetchLoading(false);
    }
  };

  const handleDropOffDateFilterChange = async (value) => {
    setFilters((prev) => ({
      ...prev,
      dropOffDateFilter: value,
      regionFilter: { value: 'ALL', label: 'ALL' },
      routesFilter: [],
    }));
    setPackingSlipsList([]);

    getLocations(value).catch((err) => {
      console.error(
        'Error while getLocations in handleDropOffDateFilterChange:',
        err,
      );
    });

    getPackingSlips(yyyymmdd(value, '-')).catch((err) => {
      console.error(
        'Error while getPackingSlips in handleDropOffDateFilterChange:',
        err,
      );
    });

    await prefetchDataForDate(value);
  };

  const handleRegionFilterChange = (value) => {
    setFilter('regionFilter', value);
    setFilter('routesFilter', []);
    filterRoutesByRegionAndCenter(
      value.value,
      filters.fulfillmentCenterFilter.value,
    );
  };

  const handleFulfillmentCenterFilterChange = (value) => {
    setFilter('fulfillmentCenterFilter', value);
    setFilter('routesFilter', []);
    filterRoutesByRegionAndCenter(filters.regionFilter.value, value.value);
  };

  const handleRoutesFilterChange = (value) => {
    setFilter('routesFilter', value);
  };

  const handleToggleAllRoutes = () => {
    if (isSearchLoading) return;
    if (filters.routesFilter === routesList) {
      setFilter('routesFilter', []);
    } else {
      setFilter('routesFilter', routesList);
    }
  };

  const filterRoutesByRegionAndCenter = (region, center, uniqueRoutesList) => {
    let filteredRoutes = uniqueRoutesList || uniqueLocationRoutes || [];

    if (region !== 'ALL') {
      filteredRoutes = filteredRoutes.filter((item) => item.market === region);
    }

    if (center !== 'ALL') {
      // Filtering routes by route id, because item.fulfillmentCenter is always null
      // Get location ids that have the selected fulfillmentCenter from opsLocations
      let locationIdsWithCenter = [];
      try {
        const rawOpsLocationsData = localStorage.getItem('opsLocations');
        if (rawOpsLocationsData) {
          const parsedOpsLocationsData =
            JSON.parse(rawOpsLocationsData)?.locations || [];
          locationIdsWithCenter = parsedOpsLocationsData
            .filter((location) => location.fulfillmentCenter === center)
            .map((location) => location.id);
        }
      } catch (err) {
        console.error('Error parsing opsLocationsData:', err);
      }

      // Filter routes by location id
      filteredRoutes = filteredRoutes.filter((item) =>
        locationIdsWithCenter.includes(item.id),
      );
    }

    setRoutesList(
      filteredRoutes.map((item) => ({
        value: item.route,
        label: item.route,
      })),
    );
  };

  const handleFilterClear = () => {
    stopInterval();
    setFilterSearched(false);
    resetFilters();
    setSearchedRoutes([]);
    setIsExpandedView(false);
    filterRoutesByRegionAndCenter('ALL', 'ALL');
    localStorage.removeItem('stockingDashFilterData');
  };

  const handleStopTypeFilterChange = (value) => {
    setFilter('stopTypeFilter', value);
  };

  const toggleExpandingView = () => {
    setIsExpandedView(!isExpandedView);
  };

  const getLocations = async (date) => {
    setIsLoading(true);
    let uniqueRoutesList = [];

    try {
      const locations = await GetLocationRoutes({
        stockingDate: yyyymmdd(date, '-'),
      });

      if (locations?.routes) {
        const uniqueRoutesSet = new Set();
        uniqueRoutesList = locations.routes.filter((item) => {
          if (!uniqueRoutesSet.has(item.route)) {
            uniqueRoutesSet.add(item.route);

            return true;
          }
          return false;
        });

        setLocationRoutes(locations.routes);
        setUniqueLocationRoutes(uniqueRoutesList);
        setRoutesList(
          uniqueRoutesList.map((item) => ({
            value: item.route,
            label: item.route,
          })),
        );
      } else {
        console.warn('No routes found in GetLocationRoutes response');
      }
    } catch (e) {
      console.error('Error in getLocations:', e);
      toast.error(
        <>
          <b>Failed fetching routes:</b>
          <br />
          {e.message}
        </>,
        toastDefaultOptions,
      );
    } finally {
      setIsLoading(false);
    }

    return uniqueRoutesList;
  };

  const loadLocalFilterData = async () => {
    if (localStorage.getItem('stockingDashFilterData')) {
      const filterData = JSON.parse(
        localStorage.getItem('stockingDashFilterData'),
      );
      const date = new Date(filterData.dropOffDateFilter);
      setFilters({ ...filterData, dropOffDateFilter: date });
      const uniqueRoutesList = await loadLocationAndPackingSlips(date);
      filterRoutesByRegionAndCenter(
        filterData.regionFilter.value,
        filterData.fulfillmentCenterFilter.value,
        uniqueRoutesList,
      );
    }
  };

  const getPackingSlips = async (dropOffDate) => {
    try {
      let items = [];
      let nextToken = null;
      do {
        const rawPackingSlips = await PaginatedPackingSlip(
          dropOffDate,
          nextToken,
        );
        if (rawPackingSlips?.data?.paginatedPackingSlips) {
          items = items.concat(
            rawPackingSlips.data.paginatedPackingSlips.packingSlips || [],
          );
          nextToken = rawPackingSlips.data.paginatedPackingSlips.nextToken;
        }
      } while (nextToken);
      setPackingSlipsList(items);
    } catch (e) {
      console.error(e);
      toast.error(
        <>
          <b>Failed fetching Packing Slips:</b>
          <br />
          {e.errors[0].message}
        </>,
        toastDefaultOptions,
      );
    }
  };

  useEffect(() => {
    const opsLocations = async () => {
      try {
        const allLocations = [];
        let nextToken = null;

        do {
          const response = await GetOpsLocationsStockingDash(nextToken, [
            'LIVE',
          ]);
          if (response?.data?.opsLocations) {
            const locations = response.data.opsLocations.locations || [];
            allLocations.push(...locations);
          }
          nextToken = response?.data?.opsLocations?.nextToken;
        } while (nextToken);

        localStorage.setItem(
          'opsLocations',
          JSON.stringify({
            date: moment().format('YYYY-MM-DD'),
            locations: allLocations,
          }),
        );
      } catch (error) {
        console.error('Error fetching ops locations:', error);
      }
    };

    const opsLocationsLocalData = localStorage.getItem('opsLocations');

    if (
      !opsLocationsLocalData ||
      JSON.parse(opsLocationsLocalData).date !== moment().format('YYYY-MM-DD')
    ) {
      opsLocations();
    }
    if (opsLocationsLocalData) {
      const uniqueFulfillmentCentersSet = new Set();
      JSON.parse(opsLocationsLocalData).locations.map((item) => {
        if (item.fulfillmentCenter !== null) {
          uniqueFulfillmentCentersSet.add(item.fulfillmentCenter);
        }
        // Extract unique fulfillment centers
        const uniqueFulfillmentCenters = Array.from(
          uniqueFulfillmentCentersSet,
        );
        const fulfillmentCentersRawList = uniqueFulfillmentCenters
          .map((center) => ({
            value: center,
            label: center,
          }))
          .filter(
            (center) =>
              center.value !== 'Does Not Exist' && center.value !== 'Not Found',
          );

        fulfillmentCentersRawList.unshift({ value: 'ALL', label: 'ALL' });

        setFulfillmentCentersList(fulfillmentCentersRawList);
      });
    } else {
      console.warn(
        'No fulfillmentCenters found in GetOpsLocationsStockingDash response',
      );
    }
  }, []);

  useEffect(() => {
    getLocations(now);
    prefetchDataForDate(now);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (isExpandedView) {
      handleSearch();
    }
  }, [isExpandedView]);

  const saveFilterDataToLocalStorage = () => {
    const filterSetOn = new Date();

    const filterData = {
      ...filters,
      filterSetOn,
    };
    localStorage.setItem('stockingDashFilterData', JSON.stringify(filterData));
  };

  const findDuplicateIds = (items) =>
    items
      .filter(
        (item, index, self) =>
          self.findIndex((t) => t.id === item.id) !== index,
      )
      .map((item) => item.id);

  const fetchStockedAndQueriedLocations = async (date) => {
    const stockingDate = yyyymmdd(date, '-');

    // Shared pipeline: drains each event type, covers the previous calendar day,
    // and filters to the 6 PM Chicago stocking window. Failures are tolerated
    // per event type so one bad query can't blank the whole dashboard.
    const {
      stockedLocationsItems,
      packingSlipQueriedItems,
      locationUnreachableItems,
      vanTemperatureItems,
      inventoryRemovalItems,
      clearOutDecommissionItems: ClearOutDecommissionItems,
      dispatchUnreachableItems,
      failedEventTypes,
    } = await fetchDriverActionsForDate(stockingDate);

    // If any events failed, show a toast
    if (failedEventTypes.length > 0) {
      const uniqueEventsList = failedEventTypes.join(', ');
      toast.warn(
        `Something went wrong with fetching the following Driver Actions: ${uniqueEventsList}`,
        toastDefaultOptions,
      );
    }

    return {
      stockedLocationsIds: stockedLocationsItems.map((item) => item.locationId),
      packingSlipQueriedIds: packingSlipQueriedItems.map(
        (item) => item.locationId,
      ),
      stockedLocationsItems,
      packingSlipQueriedItems,
      locationUnreachableItems,
      vanTemperatureItems,
      inventoryRemovalItems,
      ClearOutDecommissionItems,
      dispatchUnreachableItems,
    };
  };

  const getFridgeHealthDataByChunks = async (locationIds) => {
    const CHUNK_SIZE = 200;

    // filter out only fridge locations
    let opsLocationsData = {};
    try {
      const rawOpsLocationsData = localStorage.getItem('opsLocations');
      if (rawOpsLocationsData) {
        const parsedOpsLocationsData =
          JSON.parse(rawOpsLocationsData)?.locations || [];
        opsLocationsData = parsedOpsLocationsData.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});
      }
    } catch (err) {
      console.error('Error parsing opsLocationsData:', err);
    }

    const fridgeLocationIds = locationIds.filter((id) => {
      const loc = opsLocationsData[id];
      return loc?.type === 'FRIDGE';
    });

    const chunks = [];
    for (let i = 0; i < fridgeLocationIds.length; i += CHUNK_SIZE) {
      chunks.push(fridgeLocationIds.slice(i, i + CHUNK_SIZE));
    }

    try {
      const requests = chunks.map((chunk) =>
        FridgeHealthStockingDash({ locationIds: chunk }),
      );

      const results = await Promise.all(requests);

      let allFridgeHealth = [];
      for (const res of results) {
        if (res?.data?.fridgeHealth?.length) {
          allFridgeHealth = [...allFridgeHealth, ...res.data.fridgeHealth];
        }
      }

      return {
        data: {
          fridgeHealth: allFridgeHealth,
        },
      };
    } catch (err) {
      console.error('FridgeHealthStockingDash error:', err);
      toast.warn(
        'Error getting fridge health data. Some data may be missing.',
        toastDefaultOptions,
      );
      return { data: { fridgeHealth: [] } };
    }
  };

  const handleSearch = async () => {
    if (refreshCount > 240) {
      toast.warn(
        'You have been on this page for a while. Please refresh the page to continue.',
        { ...toastDefaultOptions, autoClose: false },
      );
      clearInterval(intervalRef.current);
      return;
    }

    setIsSearchLoading(true);
    setRefreshCount(refreshCount + 1);
    saveFilterDataToLocalStorage();

    let currentLocationRoutes = locationRoutes.filter((route) =>
      filters.routesFilter.some(
        (filterItem) => filterItem.value === route.route,
      ),
    );

    // Retailer filter logic
    if (filters.retailerFilter && filters.retailerFilter.length > 0) {
      const allowedRetailerIds = filters.retailerFilter.flatMap((r) => r.value);
      currentLocationRoutes = currentLocationRoutes.filter((route) =>
        allowedRetailerIds.some((id) => id === route.id),
      );
    }
    // Stop Type filter logic
    if (filters.stopTypeFilter && filters.stopTypeFilter.length > 0) {
      const allowedTypes = filters.stopTypeFilter.map((t) => t.value);
      currentLocationRoutes = currentLocationRoutes.filter((route) =>
        allowedTypes.includes(route.stopType),
      );
    }
    const currentLocationsList = currentLocationRoutes.map((item) => item.id);

    try {
      let queryLocationsData = null;
      const dateOfFilter = filters.dropOffDateFilter?.getTime?.();

      if (
        prefetchedLocationsData &&
        prefetchedDate &&
        !prefetchLoading &&
        prefetchedDate?.getTime() === dateOfFilter
      ) {
        // use prefetched data if available and date matches
        queryLocationsData = prefetchedLocationsData;
      } else {
        // fetch data if not prefetched or date doesn't match
        try {
          queryLocationsData = await fetchStockedAndQueriedLocations(
            filters.dropOffDateFilter,
          );
        } catch (err) {
          console.error('fetchStockedAndQueriedLocations error:', err);
          toast.warn(
            'Error fetching stocked/queried locations. Some data may be missing.',
            toastDefaultOptions,
          );
          queryLocationsData = {
            stockedLocationsIds: [],
            packingSlipQueriedIds: [],
            stockedLocationsItems: [],
            packingSlipQueriedItems: [],
            locationUnreachableItems: [],
            vanTemperatureItems: [],
            inventoryRemovalItems: [],
            ClearOutDecommissionItems: [],
            dispatchUnreachableItems: [],
          };
        }
      }

      let fridgeHealthDataRaw = { data: { fridgeHealth: [] } };
      if (isExpandedView) {
        fridgeHealthDataRaw = await getFridgeHealthDataByChunks(
          currentLocationsList,
        );
      }

      const {
        stockedLocationsIds,
        packingSlipQueriedIds,
        stockedLocationsItems,
        packingSlipQueriedItems,
        locationUnreachableItems,
        vanTemperatureItems,
        inventoryRemovalItems,
        ClearOutDecommissionItems,
        dispatchUnreachableItems,
      } = queryLocationsData;

      const locationsHealthData = fridgeHealthDataRaw.data.fridgeHealth
        .map((data) => data.fridgeData[0])
        .filter(Boolean);

      const duplicateIds = findDuplicateIds(currentLocationRoutes);

      const extendedRoutes = extendRoutesWithAdditionalData(
        locationsHealthData,
        stockedLocationsIds,
        packingSlipQueriedIds,
        stockedLocationsItems,
        packingSlipQueriedItems,
        locationUnreachableItems,
        vanTemperatureItems,
        inventoryRemovalItems,
        ClearOutDecommissionItems,
        dispatchUnreachableItems,
        packingSlipsList,
        duplicateIds,
        currentLocationRoutes,
      );

      const groupedAndSortedRoutes = groupAndSortRoutes(extendedRoutes);
      setSearchedRoutes(groupedAndSortedRoutes);

      startInterval();

      setPrefetchedLocationsData(null);
      setPrefetchedDate(null);
    } catch (e) {
      console.error(e);
      toast.error(
        'Unhandled error during parallel fetches:',
        toastDefaultOptions,
      );
    }

    setFilterSearched(true);
    setIsSearchLoading(false);
    setLastUpdated(moment().format('h:mm:ss A'));
  };

  const updateRouteName = (packingSlipsData, currentRoute, partId) => {
    const matchingPackingSlip = packingSlipsData.find((slip) => {
      const [, middlePart] = slip.uniqueId.split('#');
      const uniqueIdPart = middlePart.split('&')[1];
      return (
        uniqueIdPart === partId &&
        slip.location &&
        slip.location.split('&')[0] === currentRoute.id
      );
    });

    return matchingPackingSlip?.driver?.includes(currentRoute.route)
      ? matchingPackingSlip.driver
      : 'none';
  };

  const parsePackingSlipId = (data) => {
    try {
      if (!data) return null;
      const packingSlipId = JSON.parse(data)?.packingSlipId;
      return packingSlipId?.split('&')[1]?.split('#')[0] || null;
    } catch (error) {
      console.error('Error parsing packing slip ID:', error);
      return null;
    }
  };

  const extendRoutesWithAdditionalData = (
    locationsHealthData,
    stockedLocationsIds,
    packingSlipQueriedIds,
    stockedLocationsItems,
    packingSlipQueriedItems,
    locationUnreachableItems,
    vanTemperatureItems,
    inventoryRemovalItems,
    ClearOutDecommissionItems,
    dispatchUnreachableItems,
    packingSlips,
    duplicateIds,
    currentLocationRoutes,
  ) => {
    // Status ladder mirrors `deriveStopStatus` in libs/stopStatus.js; a rule
    // change here must be made there too.
    let opsLocationsData = {};
    try {
      const rawOpsLocationsData = localStorage.getItem('opsLocations');
      if (rawOpsLocationsData) {
        const parsedOpsLocationsData =
          JSON.parse(rawOpsLocationsData)?.locations || [];
        opsLocationsData = parsedOpsLocationsData.reduce((acc, item) => {
          acc[item.id] = item; // item.id should match route.id
          return acc;
        }, {});
      }
    } catch (err) {
      console.error('Error parsing locationsExtraData from LocalStorage:', err);
    }

    const uniqueQueriedItems = Array.from(
      new Set(packingSlipQueriedItems.map((item) => JSON.stringify(item))),
    ).map((item) => JSON.parse(item));

    return currentLocationRoutes
      .filter((route) =>
        filters.routesFilter.some(
          (filterItem) => filterItem.value === route.route,
        ),
      )
      .map((route) => {
        let status = 'NOT_STARTED';
        let stockedAt = '';
        let scannedAt = '';
        let driver = '';
        let routeName = route.route;
        let queriedItem = null;
        let safetyHold = '';
        let vendingState = '';

        // Check for Fridge Health Data
        const currentHealthData = locationsHealthData.find(
          (item) => item.locationId === route.id,
        );
        safetyHold =
          currentHealthData?.safetyHold === true
            ? 'Yes'
            : currentHealthData?.safetyHold === false
            ? 'No'
            : '';
        vendingState = currentHealthData?.jofemarState || '';

        // Check for VanTemperature
        const vanTemperatureIds = vanTemperatureItems
          ? vanTemperatureItems.map((item) => item.locationId)
          : [];

        if (vanTemperatureIds.includes(route.id)) {
          const vanTempItem = vanTemperatureItems.find(
            (item) => item.locationId === route.id,
          );
          if (vanTempItem) {
            status = 'VAN_TEMPERATURE_SET';
          }
        }

        // Check if location is scanned
        if (packingSlipQueriedIds.includes(route.id)) {
          const queriedItems = uniqueQueriedItems.filter(
            (item) => item.locationId === route.id,
          );

          // Handling locations that repetitive in the different routes
          if (queriedItems.length > 1) {
            queriedItems.forEach((item) => {
              const packingSlipId = parsePackingSlipId(item.data);
              const matchingPackingSlip = packingSlips.filter(
                (slip) =>
                  slip.uniqueId.includes(packingSlipId) &&
                  slip.driver.includes(route.route),
              );
              if (matchingPackingSlip.length === 1) {
                queriedItem = item;
                routeName = matchingPackingSlip[0].driver;
              } else if (matchingPackingSlip.length > 1) {
                queriedItem = queriedItems.reduce((latest, itemObj) =>
                  itemObj._lastModified > latest._lastModified
                    ? itemObj
                    : latest,
                );
              }
            });
          } else if (queriedItems.length === 1) {
            queriedItem = queriedItems[0];
          }

          if (queriedItem) {
            // set IN_PROGRESS status if needed
            const packingSlipIdPart = parsePackingSlipId(queriedItem.data);
            scannedAt = moment
              .unix(queriedItem._lastModified)
              .tz('America/Chicago')
              .format('ddd h:mm A');
            driver = JSON.parse(queriedItem.data).driver?.split('@')[0];
            if (duplicateIds.includes(route.id)) {
              routeName = updateRouteName(
                packingSlips,
                route,
                packingSlipIdPart,
              );
            }

            status = 'IN_PROGRESS';

            // set NOT SUBMITTED status if needed
            const isLaterItemPresent = uniqueQueriedItems.some(
              (packingSlipItem) => {
                const packingSlipItemDriver =
                  JSON.parse(packingSlipItem.data).driver || '';
                const queriedItemDriver =
                  JSON.parse(queriedItem.data).driver || '';
                return (
                  packingSlipItem?._lastModified > queriedItem?._lastModified &&
                  packingSlipItemDriver === queriedItemDriver
                );
              },
            );

            if (isLaterItemPresent) {
              status = 'INCOMPLETE';
            }
          }
        }

        // Check if location is stocked
        if (stockedLocationsIds.includes(route.id)) {
          const stockedLocation = stockedLocationsItems.find(
            (item) => item.locationId === route.id,
          );
          if (stockedLocation) {
            status = 'COMPLETED';

            stockedAt = moment
              .unix(stockedLocation._lastModified)
              .tz('America/Chicago')
              .format('ddd h:mm A');
          }
        }

        // Check if location is unreachable
        const locationUnreachableIds = locationUnreachableItems.map(
          (item) => item.locationId,
        );

        if (
          locationUnreachableIds.includes(route.id) &&
          !stockedLocationsIds.includes(route.id)
        ) {
          const unreachableItem = locationUnreachableItems.find(
            (item) => item.locationId === route.id,
          );

          if (unreachableItem) {
            driver = JSON.parse(unreachableItem.data).driver?.split('@')[0];

            // Check if there's a corresponding PackingSlipQuery
            const hasQueriedItem = packingSlipQueriedIds.includes(route.id);
            status = hasQueriedItem
              ? 'UNREACHABLE_COMPLETE'
              : 'UNREACHABLE_INCOMPLETE';
          }
        } else if (!stockedLocationsIds.includes(route.id)) {
          // Dispatch-coded from the Supervisor Dashboard; rows are newest-first.
          const dispatchItem = dispatchUnreachableItems.find(
            (item) => item?.data?.locationId === route.id,
          );

          if (dispatchItem) {
            driver = dispatchItem.data.supervisorEmail?.split('@')[0] || '';
            status = packingSlipQueriedIds.includes(route.id)
              ? 'UNREACHABLE_COMPLETE'
              : 'UNREACHABLE_INCOMPLETE';
          }
        }

        // Check if location is Clearout (`InventoryRemoval` driver action)
        const inventoryRemovalIds = inventoryRemovalItems.map(
          (item) => item.locationId,
        );

        if (inventoryRemovalIds.includes(route.id)) {
          const inventoryRemovalItem = inventoryRemovalItems.find(
            (item) => item.locationId === route.id,
          );
          if (inventoryRemovalItem) {
            driver = JSON.parse(inventoryRemovalItem.data).contactName?.split(
              '@',
            )[0];
            status = 'CLEAROUT_COMPLETE';
          }
        }

        // Check if location is Decommission (`ClearOutDecommission` driver action)
        const ClearOutDecommissionIds = ClearOutDecommissionItems.map(
          (item) => item.locationId,
        );

        if (ClearOutDecommissionIds.includes(route.id)) {
          const ClearOutDecommissionItem = ClearOutDecommissionItems.find(
            (item) => item.locationId === route.id,
          );
          if (ClearOutDecommissionItem) {
            driver = JSON.parse(
              ClearOutDecommissionItem.data,
            ).contactName?.split('@')[0];
            status = 'CLEAROUT_DECOMMISSION_COMPLETE';
          }
        }

        const extraItem = opsLocationsData[route.id];
        let locationType = '';
        let locationAccountTier = '';
        let locationOnlineDate = '';
        if (extraItem) {
          locationType = extraItem.type || '';
          locationAccountTier = extraItem.accountTier || '';
          locationOnlineDate = extraItem.onlineDate || '';
        }

        return {
          ...route,
          status,
          stockedAt,
          scannedAt,
          driver,
          routeName,
          safetyHold,
          vendingState,
          type: locationType,
          accountTier: locationAccountTier,
          onlineDate: locationOnlineDate,
        };
      });
  };

  const groupAndSortRoutes = (extendedRoutes) => {
    return Object.values(
      extendedRoutes.reduce((result, item) => {
        (result[item.route] = result[item.route] || {
          route: item.route,
          locations: [],
        }).locations.push(item);
        return result;
      }, {}),
    ).map((group) => ({
      ...group,
      locations: group.locations.sort((a, b) => a.stop - b.stop),
    }));
  };

  return (
    <>
      <PageHeader>Stocking Real-Time Dashboard</PageHeader>
      {filterSearched ? (
        <>
          <div className="filtersSearched">
            <div className="appliedFilters">
              <b>Drop Off Date: </b> {yyyymmdd(filters.dropOffDateFilter, '-')}{' '}
              ({moment(filters.dropOffDateFilter).format('ddd')}) <br />
              <b>Region:</b> {filters.regionFilter.value} <br />
              <b>Market:</b> {filters.fulfillmentCenterFilter.value} <br />
              <b>Routes:</b> {filters.routesFilter.length} <br />
              {filters?.stopTypeFilter?.length > 0 && (
                <>
                  <b>Stop type: </b>
                  {filters.stopTypeFilter.map((type) => type.label).join(', ')}
                  <br />
                </>
              )}
              {filters?.retailerFilter?.length > 0 && (
                <>
                  <b>Retailer: </b>
                  {filters.retailerFilter.map((type) => type.label).join(', ')}
                  <br />
                </>
              )}
            </div>
            <div className="filterActions">
              <Button bsStyle="info" onClick={() => handleFilterClear()}>
                Update Route Filters
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="filterWrapper">
            <div className="inputWrapper">
              <div className="inputLabel">Drop Off Date</div>
              <DatePicker
                className="datePicker"
                onChange={handleDropOffDateFilterChange}
                value={filters.dropOffDateFilter}
                disabled={isLoading || isSearchLoading}
                format="MM/dd/y"
              />
            </div>
            <div className="inputWrapper">
              <div className="inputLabel">Region</div>

              <Select
                options={regionsList}
                getOptionLabel={(option) => option.value}
                closeMenuOnSelect
                hideSelectedOptions={false}
                onChange={handleRegionFilterChange}
                isLoading={isLoading}
                isDisabled={isLoading || isSearchLoading}
                value={filters.regionFilter}
                maxMenuHeight={400}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '0px',
                    border: '1px solid gray',
                  }),
                }}
              />
            </div>

            <div className="inputWrapper">
              <div className="inputLabel">Market</div>

              <Select
                options={fulfillmentCentersList}
                getOptionLabel={(option) => option.value}
                closeMenuOnSelect
                hideSelectedOptions={false}
                onChange={handleFulfillmentCenterFilterChange}
                isLoading={isLoading}
                isDisabled={isLoading || isSearchLoading}
                value={filters.fulfillmentCenterFilter}
                maxMenuHeight={400}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '0px',
                    border: '1px solid gray',
                  }),
                }}
              />
            </div>

            <div className="inputWrapper">
              <div className="inputLabel">Stop Type</div>
              <Select
                options={STOP_TYPES}
                getOptionLabel={(option) => option.label}
                placeholder="Select stop type (optional)"
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={handleStopTypeFilterChange}
                isLoading={isLoading}
                isDisabled={isLoading || isSearchLoading}
                value={filters.stopTypeFilter}
                maxMenuHeight={400}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '0px',
                    border: '1px solid gray',
                  }),
                }}
              />
            </div>
            <div className="inputWrapper">
              <div className="inputLabel">Retailer</div>
              <Select
                placeholder="Select retailer (optional)"
                options={Object.entries(retailerList).map(
                  ([retailerName, ids]) => ({
                    value: ids,
                    label: retailerName,
                  }),
                )}
                getOptionLabel={(option) => option.label}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={handleRetailerFilterChange}
                isLoading={retailersLoading}
                isDisabled={isLoading || isSearchLoading}
                value={filters.retailerFilter}
                maxMenuHeight={400}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '0px',
                    border: '1px solid gray',
                  }),
                }}
              />
            </div>
          </div>
          <div className="searchWrapper" style={{ width: '100%' }}>
            <div className="inputWrapper" style={{ margin: '0' }}>
              <div className="inputLabel">
                <span>Routes {!isLoading && `(${routesList.length})`}</span>
                <span
                  className={`inputActionButton ${
                    isLoading ? 'disabled' : ''
                  }`.trim()}
                  onClick={() => handleToggleAllRoutes()}
                >
                  (
                  {filters.routesFilter === routesList
                    ? 'Deselect ALL'
                    : 'Select ALL'}
                  )
                </span>
              </div>
              <Select
                options={routesList}
                getOptionLabel={(option) => option.value}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={handleRoutesFilterChange}
                isLoading={isLoading}
                isDisabled={isLoading || isSearchLoading}
                value={
                  filters.routesFilter.length ? filters.routesFilter : null
                }
                maxMenuHeight={400}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '0px',
                    border: '1px solid gray',
                    width: '100%',
                    marginBottom: '30px',
                  }),
                }}
              />
            </div>
          </div>
          <LoaderButton
            bsStyle="primary"
            bsSize="large"
            className="searchButton "
            onClick={() => handleSearch()}
            disabled={isLoading || !filters.routesFilter.length}
            isLoading={isSearchLoading}
            text="Search"
          />
        </>
      )}

      <div>
        {searchedRoutes.length === 0 ? (
          filterSearched && (
            <div
              style={{
                textAlign: 'center',
                margin: '40px 0',
                fontWeight: 'bold',
              }}
            >
              No routes found for the selected filters. Try adjusting your
              filters and searching again.
            </div>
          )
        ) : (
          <>
            <div className="summaryTitle">
              <div>
                <h2> Summary </h2>
                <div
                  className="lastUpdatedWrapper"
                  onClick={() => handleSearch()}
                >
                  As of {lastUpdated} <RefreshIcon spin={isSearchLoading} />
                </div>
              </div>
              <Button onClick={() => toggleExpandingView()}>
                {isExpandedView ? 'Collapsed' : 'Expanded'} View
              </Button>
            </div>
            <div
              className={`cardsWrapper ${
                !isExpandedView ? 'collapsed' : 'expanded'
              }`}
            >
              {searchedRoutes.map((route) => (
                <div
                  key={route.route}
                  className={`card ${
                    !isExpandedView ? 'collapsed' : 'expanded'
                  }`}
                >
                  <h3>{route.route}</h3>

                  <Table striped bordered className="routeDashboardTable">
                    <thead>
                      <tr>
                        <th className="cellStop">Stop</th>
                        <th className="cellLocation">Location</th>
                        <th
                          className={`cellScannedTime ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Scanned At <br /> (CT)
                        </th>
                        <th
                          className={`cellStockedTime ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Stocked At <br /> (CT)
                        </th>
                        <th
                          className={`cellAccountTier ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Account Tier
                        </th>
                        <th
                          className={`cellOnlineDate ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          First <br /> Stocking Date
                        </th>
                        <th
                          className={`cellSafetyHold ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Safety Hold
                        </th>
                        <th
                          className={`cellVendingState ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Vending State <br /> Status
                        </th>
                        <th className="cellStatus">Status</th>
                        <th
                          className={`cellDriver ${
                            !isExpandedView ? 'hide' : ''
                          }`}
                        >
                          Driver
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {route.locations.length === 0 ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: 'center' }}>
                            No locations found for this route and filters. Try
                            again.
                          </td>
                        </tr>
                      ) : (
                        route.locations.map((location) => (
                          <tr key={`${location.id}-${location.stop}`}>
                            <td className="cellStop">{location.stop}</td>

                            <td className="cellLocation">
                              {`${location.id}`}
                              {`${
                                location.stopType === 'REMOVAL'
                                  ? ' (removal)'
                                  : location.stopType ===
                                    'CLEAROUT_DECOMMISSION'
                                  ? ' (decommission)'
                                  : ''
                              }`}
                            </td>
                            <td
                              className={`cellScannedTime ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.scannedAt}
                            </td>
                            <td
                              className={`cellStockedTime ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.stockedAt}
                            </td>
                            <td
                              className={`cellAccountTier ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {formatAccountTier(location.accountTier)}
                            </td>
                            <td
                              className={`cellOnlineDate ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.onlineDate}
                            </td>
                            <td
                              className={`cellSafetyHold ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.safetyHold}
                            </td>
                            <td
                              className={`cellVendingState ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.vendingState && (
                                <TextLabel
                                  type="secondary"
                                  text={
                                    VENDING_STATUSES[location.vendingState]
                                      ?.label || location.vendingState
                                  }
                                  color={
                                    VENDING_STATUSES[location.vendingState]
                                      ?.color || 'lightGray'
                                  }
                                  tooltip={
                                    VENDING_STATUSES[location.vendingState]
                                      ?.description || ''
                                  }
                                />
                              )}
                            </td>

                            <td className="cellStatus">
                              <TextLabel
                                text={STATUSES[location.status].label}
                                color={STATUSES[location.status].color}
                                small={!isExpandedView}
                                tooltip={STATUSES[location.status].description}
                              />
                            </td>
                            <td
                              className={`cellDriver ${
                                !isExpandedView ? 'hide' : ''
                              }`}
                            >
                              {location.driver}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {isSearchLoading && filterSearched && (
        <div className="loadingMessage">
          Data is refreshing now... <RefreshIcon spin />
        </div>
      )}
    </>
  );
};

export default ViewStockingDashboard;
